/** React-free leaf: the date formatter set, built for one locale + zone. */

export interface DateFormatters {
  /** 24.12.2026 · 24/12/2026 */
  readonly date: (value: Date | number) => string;
  /** 24.12.2026, 14:30 · 24/12/2026, 14:30 */
  readonly dateTime: (value: Date | number) => string;
  /** 14:30 */
  readonly time: (value: Date | number) => string;
  /** 24. Dezember 2026 · 24 December 2026 */
  readonly long: (value: Date | number) => string;
  /** 24. Dez. 2026 · 24 Dec 2026 */
  readonly medium: (value: Date | number) => string;
  /** Sep. 2026 · Sept 2026 */
  readonly monthYear: (value: Date | number) => string;
  /** 07.03. · 07/03 */
  readonly dayMonth: (value: Date | number) => string;
  /** 1. April · 1 April — yearly key date without year (e.g. main due date). */
  readonly dayMonthLong: (value: Date | number) => string;
  /** vor 3 Tagen · 3 days ago */
  readonly relative: (value: Date | number, now?: Date | number) => string;
  /** Freitag, 2. Oktober 2026 · Friday, 2 October 2026 */
  readonly weekdayLong: (value: Date | number) => string;
  /** Oktober · October */
  readonly monthLong: (value: Date | number) => string;
  /** Okt. · Oct */
  readonly monthShort: (value: Date | number) => string;
  /** Oktober 2026 · October 2026 */
  readonly monthYearLong: (value: Date | number) => string;
  /** Fr · Fri (trailing dot dropped for column headers) */
  readonly weekdayShort: (value: Date | number) => string;
}

/** `timeZone` undefined = device zone. */
export function createDateFormatters(args: {
  readonly localeTag: string;
  readonly timeZone: string | undefined;
  readonly relative: DateFormatters["relative"];
}): DateFormatters {
  const { localeTag, timeZone, relative } = args;
  // Not `format.dateTime`: next-intl passes bare `en`, which Intl renders US-style.
  const formatter = (
    options: Intl.DateTimeFormatOptions,
  ): ((value: Date | number) => string) => {
    const intl = new Intl.DateTimeFormat(
      localeTag,
      timeZone === undefined ? options : { ...options, timeZone },
    );
    return (value) => intl.format(value);
  };
  const weekdayShortBase = formatter({ weekday: "short" });

  return {
    date: formatter({ day: "2-digit", month: "2-digit", year: "numeric" }),
    dateTime: formatter({
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    time: formatter({ hour: "2-digit", minute: "2-digit" }),
    long: formatter({ day: "numeric", month: "long", year: "numeric" }),
    medium: formatter({ day: "numeric", month: "short", year: "numeric" }),
    monthYear: formatter({ month: "short", year: "numeric" }),
    dayMonth: formatter({ day: "2-digit", month: "2-digit" }),
    dayMonthLong: formatter({ day: "numeric", month: "long" }),
    relative,
    weekdayLong: formatter({
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    monthLong: formatter({ month: "long" }),
    monthShort: formatter({ month: "short" }),
    monthYearLong: formatter({ month: "long", year: "numeric" }),
    weekdayShort: (value) => weekdayShortBase(value).replace(/\.$/, ""),
  };
}
